/**
 * Hooks React Query pour les analytics d'établissement
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import * as analyticsService from '../services/analytics.service';
import type {
  EstablishmentSummary,
  ClassMetrics,
  GradeMetrics,
  TopActivity,
  DailyActivity,
} from '@sphere/analytics/domain';
import { TopActivitiesResponseDto } from '@sphere/analytics/domain';

export const establishmentAnalyticsKeys = {
  all: ['analytics', 'establishment'] as const,
  dashboard: (tenantId: string, sinceDays: number) =>
    [...establishmentAnalyticsKeys.all, 'dashboard', tenantId, sinceDays] as const,
  byClass: (tenantId: string, sinceDays: number) =>
    [...establishmentAnalyticsKeys.all, 'by-class', tenantId, sinceDays] as const,
  byGrade: (tenantId: string, sinceDays: number) =>
    [...establishmentAnalyticsKeys.all, 'by-grade', tenantId, sinceDays] as const,
  topActivities: (tenantId: string, sinceDays: number, limit: number) =>
    [...establishmentAnalyticsKeys.all, 'top-activities', tenantId, sinceDays, limit] as const,
  dailyActivity: (tenantId: string, sinceDays: number) =>
    [...establishmentAnalyticsKeys.all, 'daily-activity', tenantId, sinceDays] as const,
};

export function useEstablishmentDashboard(
  tenantId: string | undefined,
  sinceDays = 30,
  options?: Omit<UseQueryOptions<EstablishmentSummary>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: establishmentAnalyticsKeys.dashboard(tenantId!, sinceDays),
    queryFn: () => analyticsService.getEstablishmentSummary(tenantId!, { since_days: sinceDays }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useEstablishmentByClass(
  tenantId: string | undefined,
  sinceDays = 30,
  options?: Omit<
    UseQueryOptions<{
      tenant_id: string;
      since_days: number;
      classes: ClassMetrics[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: establishmentAnalyticsKeys.byClass(tenantId!, sinceDays),
    queryFn: () => analyticsService.getEstablishmentByClass(tenantId!, { since_days: sinceDays }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useEstablishmentByGrade(
  tenantId: string | undefined,
  sinceDays = 30,
  options?: Omit<
    UseQueryOptions<{
      tenant_id: string;
      since_days: number;
      grades: GradeMetrics[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: establishmentAnalyticsKeys.byGrade(tenantId!, sinceDays),
    queryFn: () => analyticsService.getEstablishmentByGrade(tenantId!, { since_days: sinceDays }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useEstablishmentTopActivities(
  tenantId: string | undefined,
  sinceDays = 30,
  limit = 10,
  options?: Omit<
    UseQueryOptions<TopActivitiesResponseDto>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: establishmentAnalyticsKeys.topActivities(tenantId!, sinceDays, limit),
    queryFn: () =>
      analyticsService.getEstablishmentTopActivities(tenantId!, {
        since_days: sinceDays,
        limit,
      }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

export function useEstablishmentDailyActivity(
  tenantId: string | undefined,
  sinceDays = 30,
  options?: Omit<
    UseQueryOptions<{
      tenant_id: string;
      since_days: number;
      daily_metrics: DailyActivity[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: establishmentAnalyticsKeys.dailyActivity(tenantId!, sinceDays),
    queryFn: () =>
      analyticsService.getEstablishmentDailyActivity(tenantId!, {
        since_days: sinceDays,
      }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}
